import { graphql, useStaticQuery } from "gatsby"
import { FluidObject } from "gatsby-image"
import React, { useContext, useEffect, useState } from "react"
import Slider, { Settings as IReactSlickSettings } from "react-slick"
import VisibilitySensor from "react-visibility-sensor"
import { css } from "styled-components"
import { IBreakpointImageVariants } from "../../design-system/atoms/breakpoint-image"
import Grid from "../../design-system/atoms/layout-primitives/grid"
import Block from "../../design-system/atoms/layout-primitives/block"
import CarouselArrow from "../../design-system/molecules/carousel-arrow"
import { UIContext } from "../shared/layout"
import SdkProjectCard from "./sdk-projects-card"
import {
  IFluidImageQueryArray,
  IPublicURLQuery,
} from "../../design-system/types/imageQuery.types"
import Breakpoints from "../shared/breakpoints"

interface ISdkProject {
  image: IBreakpointImageVariants
  logo: FluidObject
}

interface ISdkProjectsQuery {
  rightArrow: IPublicURLQuery
  leftArrow: IPublicURLQuery
  mobile: IFluidImageQueryArray
  desktop: IFluidImageQueryArray
  logos: IFluidImageQueryArray
}

export default function SdkProjectsCarousel() {
  const { rightArrow, leftArrow, mobile, desktop, logos } =
    useStaticQuery<ISdkProjectsQuery>(graphql`
      query {
        rightArrow: file(
          relativePath: { eq: "0_Global_Assets/desktop/slider_arrow_right.svg" }
        ) {
          publicURL
        }
        leftArrow: file(
          relativePath: { eq: "0_Global_Assets/desktop/slider_arrow_left.svg" }
        ) {
          publicURL
        }
        mobile: allFile(
          filter: {
            relativeDirectory: {
              eq: "1_SOLUTIONS/Developer Tools/Mobile/sdk_projects"
            }
          }
          sort: { fields: base }
        ) {
          edges {
            node {
              id
              childImageSharp {
                fluid(maxHeight: 512, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
        desktop: allFile(
          filter: {
            relativeDirectory: {
              eq: "1_SOLUTIONS/Developer Tools/Desktop/sdk_projects"
            }
          }
          sort: { fields: base }
        ) {
          edges {
            node {
              id
              childImageSharp {
                fluid(maxHeight: 512, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
        logos: allFile(
          filter: {
            relativeDirectory: {
              eq: "1_SOLUTIONS/Developer Tools/Desktop/sdk_project_logos"
            }
          }
          sort: { fields: base }
        ) {
          edges {
            node {
              id
              childImageSharp {
                fluid(maxHeight: 64, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    `)

  const getImage = (index: number): IBreakpointImageVariants => {
    return {
      sm: mobile.edges[index].node.childImageSharp.fluid,
      md: desktop.edges[index].node.childImageSharp.fluid,
    }
  }

  const sdkProjects: ISdkProject[] = [
    {
      image: getImage(0),
      logo: logos.edges[0].node.childImageSharp.fluid,
    },
    {
      image: getImage(1),
      logo: logos.edges[1].node.childImageSharp.fluid,
    },
    {
      image: getImage(2),
      logo: logos.edges[2].node.childImageSharp.fluid,
    },
    {
      image: getImage(3),
      logo: logos.edges[3].node.childImageSharp.fluid,
    },
    {
      image: getImage(4),
      logo: logos.edges[4].node.childImageSharp.fluid,
    },
    {
      image: getImage(5),
      logo: logos.edges[5].node.childImageSharp.fluid,
    },
    {
      image: getImage(6),
      logo: logos.edges[6].node.childImageSharp.fluid,
    },
    {
      image: getImage(7),
      logo: logos.edges[7].node.childImageSharp.fluid,
    },
    {
      image: getImage(8),
      logo: logos.edges[8].node.childImageSharp.fluid,
    },
  ]
  const {
    state: { clientLoaded },
  } = useContext(UIContext)

  const [isVisible, setVisible] = useState(false)

  const slider = React.useRef(null)

  useEffect(() => {
    if (isVisible) {
      slider?.current?.slickPlay()
    } else {
      slider?.current?.slickPause()
    }
  }, [isVisible])

  const sliderSettings: IReactSlickSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: (
      <CarouselArrow
        className={"next"}
        onClick={slider?.current?.slickNext}
        imageURL={rightArrow.publicURL}
      />
    ),
    prevArrow: (
      <CarouselArrow
        className={"prev"}
        onClick={slider?.current?.slickPrev}
        imageURL={leftArrow.publicURL}
      />
    ),
    autoplaySpeed: 5000, // 5 seconds
  }
  return (
    <Grid>
      <Block
        variants={[
          css`
            grid-column: 1 / -1;
            margin-bottom: 25px;
            .slick-track {
              display: flex;
              align-items: center;
            }
          `,
          null,
          css`
            grid-column: 2/-2;
            @media (min-width: ${Breakpoints.lgPlus}) {
              grid-column: 3 / -3;
            }
          `,
        ]}
      >
        {clientLoaded && (
          <VisibilitySensor
            minTopValue={200}
            partialVisibility={true}
            onChange={(visible) => setVisible(visible)}
          >
            <Slider ref={slider} {...sliderSettings}>
              {sdkProjects.map((project, idx) => (
                <SdkProjectCard
                  key={idx}
                  fluidPrimary={project.image}
                  fluidLogo={project.logo}
                />
              ))}
            </Slider>
          </VisibilitySensor>
        )}
      </Block>
    </Grid>
  )
}
